import { useNavigate, useLocation } from "react-router-dom";

import type { AuthUser } from "@/types";
import { isSuperAdmin, canSeeTab } from "@/types";
import { BrandLogo } from "@/components/shared/common/BrandLogo";
import { NotificationBell } from "@/components/shared/common/NotificationBell";
import { ProfileCard } from "@/components/shared/card/ProfileCard";

interface AppNavbarProps {
  authUser: AuthUser;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onLogout: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
}

export function AppNavbar({ authUser, sidebarOpen, onToggleSidebar, onLogout, theme, onToggleTheme }: AppNavbarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <header className="z-20 flex h-14 w-full shrink-0 items-center justify-between border-b border-surface-secondary bg-white px-3 sm:px-5">
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          onClick={onToggleSidebar}
          className="flex h-8 w-8 items-center justify-center rounded-btn text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
          title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        {!isSuperAdmin(authUser) && authUser.org_name ? (
          <div className="flex items-center gap-2">
            {authUser.org_logo && (
              <img
                src={authUser.org_logo}
                alt={authUser.org_name}
                className="h-8 w-14 rounded-btn bg-white object-contain"
              />
            )}
            <div>
              <h1 className="text-base sm:text-lg font-bold leading-tight text-near-black">
                {authUser.org_name}
              </h1>
              <p className="hidden sm:block text-[10px] font-medium text-text-secondary">
                Applicant Intelligent Tracking System
              </p>
            </div>
          </div>
        ) : (
          <BrandLogo imgClassName="h-16 w-auto" />
        )}
      </div>

      {canSeeTab(authUser, "screen") && (
        <button
          onClick={() => navigate("/screen")}
          className={[
            "flex items-center gap-2 rounded-btn px-3 sm:px-5 py-2 text-sm font-medium transition-colors",
            pathname.startsWith("/screen")
              ? "bg-rausch text-white"
              : "bg-near-black text-white hover:bg-rausch",
          ].join(" ")}
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
          </svg>
          <span className="hidden sm:inline">Screen</span>
        </button>
      )}

      <div className="flex items-center gap-2 sm:gap-3">
        <NotificationBell />
        <div className="h-6 w-px bg-surface-secondary hidden sm:block" />
        <ProfileCard
          authUser={authUser}
          onLogout={onLogout}
          onGoToSettings={() => navigate("/settings")}
          theme={theme}
          onToggleTheme={onToggleTheme}
        />
      </div>
    </header>
  );
}
